import React from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'isomorphic-dompurify';
import styled from 'styled-components';
import { PortableText } from '@portabletext/react';
import SanityImage from 'gatsby-plugin-sanity-image';

import Text from 'components/Text';
import EmbeddedVideo from 'components/EmbeddedVideo';
import CodeBlock from 'components/CodeBlock';
import Expander from 'components/Expander';

import { color } from 'styles/utils';

const StyledHeader = styled(Text)`
  * + & {
    margin-top: 1.5rem;
  }
`;

const StyledParagraph = styled(Text)`
  * + & {
    margin-top: 1.5rem;
  }
`;

const StyledList = styled(Text)`
  padding-left: 1.5rem;

  * + & {
    margin-top: 1.5rem;
  }
`;

const StyledImage = styled.div`
  margin-top: 1.5rem;
`;

const StyledVideo = styled(EmbeddedVideo)`
  * + & {
    margin-top: 1.5rem;
  }
`;

const StyledLink = styled(Text)`
  text-decoration: underline;
  color: ${color('teal')};
`;

const components = {
  block: {
    h1: ({ children, value }) => {
      return (
        <StyledHeader
          forwardedAs="h1"
          variant="f1.resources"
          id={`id-${value._key}`}
        >
          {children}
        </StyledHeader>
      );
    },
    h2: ({ children, value }) => {
      return (
        <StyledHeader
          forwardedAs="h2"
          variant="f2.resources"
          id={`id-${value._key}`}
        >
          {children}
        </StyledHeader>
      );
    },
    h3: ({ children, value }) => {
      return (
        <StyledHeader
          forwardedAs="h3"
          variant="f3.resources"
          id={`id-${value._key}`}
        >
          {children}
        </StyledHeader>
      );
    },
    h4: ({ children, value }) => {
      return (
        <StyledHeader forwardedAs="h4" variant="f4" id={`id-${value._key}`}>
          {children}
        </StyledHeader>
      );
    },
    normal: ({ children }) => {
      if (!children[0]) {
        return <br />;
      }
      return (
        <StyledParagraph forwardedAs="p" variant="p.resources">
          {children}
        </StyledParagraph>
      );
    },
  },
  types: {
    image: ({ value }) => {
      return (
        <StyledImage>
          <Expander hiddenTrigger="Click to expand image">
            <SanityImage {...value} alt="" />
          </Expander>
        </StyledImage>
      );
    },
    code: ({ value }) => {
      return <CodeBlock {...value} />;
    },
    youtube: ({ value }) => {
      return <StyledVideo src={value.href} title="" />;
    },
    html: ({ value }) => {
      return (
        <div
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value.html) }}
        />
      );
    },
  },
  marks: {
    link: ({ children, value }) => (
      <StyledLink forwardedAs="a" variant="p.resources" {...value}>
        {children}
      </StyledLink>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <StyledList forwardedAs="ul" variant="p.resources">
        {children}
      </StyledList>
    ),
    number: ({ children }) => (
      <StyledList forwardedAs="ol" variant="p.resources">
        {children}
      </StyledList>
    ),
  },
};

export default function RichText({ value }) {
  return <PortableText value={value} components={components} />;
}

RichText.propTypes = {
  value: PropTypes.array,
};
